import { create } from 'zustand'

export type StationListenerCountSettings = {
  enabled: boolean
  endpoint: string | null
  loaded: boolean
}

type StationListenerCountEntryInput = {
  enabled: boolean
  endpoint: string | null
}

type StationListenerCountState = {
  listenerCounts: Record<number, StationListenerCountSettings>
  setListenerCountSettings: (stationId: number, settings: StationListenerCountEntryInput) => void
  setManyListenerCountSettings: (entries: Array<{ stationId: number; settings: StationListenerCountEntryInput }>) => void
  clearListenerCountSettings: (stationId: number) => void
  reset: () => void
}

function normalizeEndpoint(endpoint: string | null | undefined): string | null {
  if (!endpoint) {
    return null
  }

  const trimmed = endpoint.trim()
  if (!trimmed) {
    return null
  }

  try {
    const url = new URL(trimmed)
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.toString()
    }
  } catch (error) {
    // Ignore invalid URLs – treat as missing
  }

  return null
}

const useStationListenerCountSettingsStore = create<StationListenerCountState>((set) => ({
  listenerCounts: {},
  setListenerCountSettings: (stationId, settings) => {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      return
    }

    set((state) => ({
      listenerCounts: {
        ...state.listenerCounts,
        [stationId]: {
          enabled: Boolean(settings.enabled),
          endpoint: normalizeEndpoint(settings.enabled ? settings.endpoint : null),
          loaded: true,
        },
      },
    }))
  },
  setManyListenerCountSettings: (entries) => {
    if (!entries.length) {
      return
    }

    set((state) => {
      const next = { ...state.listenerCounts }

      for (const entry of entries) {
        if (!Number.isFinite(entry.stationId) || entry.stationId <= 0) {
          continue
        }

        next[entry.stationId] = {
          enabled: Boolean(entry.settings.enabled),
          endpoint: normalizeEndpoint(entry.settings.enabled ? entry.settings.endpoint : null),
          loaded: true,
        }
      }

      return { listenerCounts: next }
    })
  },
  clearListenerCountSettings: (stationId) => {
    if (!Number.isFinite(stationId) || stationId <= 0) {
      return
    }

    set((state) => {
      if (!(stationId in state.listenerCounts)) {
        return state
      }

      const next = { ...state.listenerCounts }
      delete next[stationId]

      return { listenerCounts: next }
    })
  },
  reset: () => set({ listenerCounts: {} }),
}))

export default useStationListenerCountSettingsStore
